/**
 * @file Affiche la fiche du client lié à une réservation, avec ses drapeaux, son statut et ses actions.
 * @module packages.common.components.customer.SelectedCustomerCard
 */

import React from 'react';
import PropTypes from 'prop-types';
import {Box, Typography, Button, Stack, Avatar, Paper, Chip, Fade, IconButton, alpha} from '@mui/material';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmailOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/EditOutlined';

import {getFormattedPhone} from 'packages/common/utils';
import {useCustomerStatus} from 'packages/common/hooks/useCustomerStatus';

import CustomerFlags from './CustomerFlags';
import CustomerStatusBadge from './CustomerStatusBadge';
import {getInitials, getClientColor} from './utils/customerUtils';

/**
 * Carte d'affichage du client sélectionné.
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.customerDetails - Détails du client sélectionné
 *   (firstName, lastName, email, et phone plat OU phonePrefix/phoneNumber de fiche,
 *   plus les drapeaux et le niveau de fidélité quand ils sont connus)
 * @param {Function} props.onClear - Callback de désélection
 * @param {Function} [props.onEdit] - Callback d'édition de la fiche ; sans lui, aucun crayon
 * @param {boolean} [props.disabled] - Désactive les actions
 * @param {Object} props.theme - Thème Material-UI
 * @return {React.Element} Carte du client sélectionné
 */
const SelectedCustomerCard = React.memo(({customerDetails, onClear, onEdit, disabled = false, theme}) => {
  // Le téléphone d'une fiche client est stocké en deux champs
  // (phonePrefix + phoneNumber) : le helper canonique gère ce format comme le
  // champ plat `phone` d'une saisie de formulaire.
  const displayPhone = getFormattedPhone(customerDetails) || customerDetails.phone || '';
  const statusConfig = useCustomerStatus(customerDetails);

  return (
    <Fade in>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          borderColor: theme.palette.primary.light,
          backgroundColor: alpha(theme.palette.primary.lighter, 0.15),
          transition: 'all 0.3s ease',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 52,
              height: 52,
              bgcolor: getClientColor(customerDetails.lastName, theme),
              fontSize: '1.25rem',
            }}
          >
            {getInitials(customerDetails.firstName, customerDetails.lastName)}
          </Avatar>

          <Box sx={{flexGrow: 1, minWidth: 0}}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {customerDetails.firstName} {customerDetails.lastName}
              </Typography>
              <CustomerFlags customer={customerDetails} size="medium" />
              <CustomerStatusBadge config={statusConfig} width={14} />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{mt: 0.5}}>
              {displayPhone && (
                <Chip icon={<PhoneIcon />} label={displayPhone} size="small" variant="outlined" sx={{borderRadius: 1}} />
              )}
              {customerDetails.email && (
                <Chip icon={<AlternateEmailIcon />} label={customerDetails.email} size="small" variant="outlined" sx={{borderRadius: 1, maxWidth: '100%'}} />
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{flexShrink: 0}}>
            {onEdit && (
              <IconButton size="small" onClick={onEdit} disabled={disabled} title="Modifier la fiche client" aria-label="Modifier la fiche client">
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            <Button variant="outlined" color="inherit" size="small" onClick={onClear} disabled={disabled} startIcon={<ClearIcon />}>
              Changer
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Fade>
  );
});

SelectedCustomerCard.propTypes = {
  customerDetails: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    phonePrefix: PropTypes.string,
    phoneNumber: PropTypes.string,
  }).isRequired,
  onClear: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  disabled: PropTypes.bool,
  theme: PropTypes.object.isRequired,
};

SelectedCustomerCard.displayName = 'SelectedCustomerCard';

export default SelectedCustomerCard;
